import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, IconButton, Typography, Stack, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import OpenWithIcon from '@mui/icons-material/OpenWith'; // Icon for drag handle
import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRemoteUsers,
} from 'agora-rtc-react';

/**
 * A draggable and resizable video call modal using Agora and Material-UI.
 * @param {object} props
 * @param {string} props.appId - The Agora App ID.
 * @param {string} props.channel - The channel name to join.
 * @param {string} props.token - The Agora token for authentication.
 * @param {string|number} props.uid - The user ID.
 * @param {boolean} props.open - Controls if the modal is open.
 * @param {function} props.onClose - Function to call when the call ends.
 * @param {boolean} [props.startWithVideoOff=false] - Optional: Whether to start with the camera off.
 */
export const VideoCall = ({ appId, channel, token, uid, open, onClose, startWithVideoOff = false }) => {
  // State for call controls
  const [calling, setCalling] = useState(true);
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(!startWithVideoOff);

  // State for window position and size
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: window.innerWidth * 0.7, height: window.innerHeight * 0.7 });
  
  // State to track dragging and resizing
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Refs for DOM elements and drag/resize offsets
  const modalRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });


  console.log("VideoCall component rendered with props:", { appId, channel, token, uid, open, startWithVideoOff });

  // --- Agora Hooks ---
  const isConnected = useIsConnected();
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);
  useJoin({ appid: appId, channel: channel, token: token, uid: uid }, calling);
  usePublish([localMicrophoneTrack, localCameraTrack]);
  const remoteUsers = useRemoteUsers();
  // --- End Agora Hooks ---

  // Effect to center the modal on initial render
  useEffect(() => {
    if (open && modalRef.current) {
      const { clientWidth, clientHeight } = modalRef.current;
      setPosition({
        x: (window.innerWidth - clientWidth) / 2,
        y: (window.innerHeight - clientHeight) / 2,
      });
    }
  }, [open]);


  // --- Event Handlers for Dragging and Resizing ---

  // Attaches global listeners for mouse move and up events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.current.x,
          y: e.clientY - dragOffset.current.y,
        });
      }
      if (isResizing) {
        setSize((prevSize) => ({
          width: Math.max(400, e.clientX - position.x), // Enforce min width
          height: Math.max(300, e.clientY - position.y), // Enforce min height
        }));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    // Cleanup function
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, position.x, position.y]);

  const handleDragMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return; // Prevent dragging on buttons
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  const handleEndCall = () => {
    setCalling(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleEndCall} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.3)' } }}>
      <Box
        ref={modalRef}
        sx={{
          position: 'absolute',
          top: position.y,
          left: position.x,
          width: size.width,
          height: size.height,
          minWidth: 400,
          minHeight: 300,
          bgcolor: '#2d2d2d',
          boxShadow: 24,
          borderRadius: 4,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Remove transform to rely on top/left for positioning
        }}
      >
        {/* --- Draggable Header --- */}
        <Box
          onMouseDown={handleDragMouseDown}
          sx={{
            height: '40px',
            bgcolor: '#3c3c3c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'move',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <OpenWithIcon sx={{ mr: 1, opacity: 0.7 }} />
          <Typography variant="body1">Video Call</Typography>
        </Box>

        {/* Main Video Area */}
        <Box sx={{ flexGrow: 1, position: 'relative', bgcolor: '#212121' }}>
          <Box sx={{ width: '100%', height: '100%', display: 'grid', gridTemplateColumns: remoteUsers.length > 1 ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: 1, p: 1 }}>
            {isConnected ? (
              remoteUsers.length > 0 ? (
                remoteUsers.map((user) => <RemoteUser key={user.uid} user={user} style={{ width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden' }} />)
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#fff' }}>Waiting for others...</Typography>
                </Box>
              )
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress color="success" />
                <Typography variant="h6" sx={{ color: '#fff', ml: 2 }}>Connecting...</Typography>
              </Box>
            )}
          </Box>

          {/* Local User (Picture-in-Picture) */}
          {isConnected && (
            <Box sx={{ position: 'absolute', bottom: 90, right: 16, width: 'clamp(120px, 20vw, 200px)', height: 'clamp(90px, 15vw, 150px)', borderRadius: 2, overflow: 'hidden', border: '2px solid rgba(255, 255, 255, 0.5)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
              <LocalUser audioTrack={localMicrophoneTrack} videoTrack={localCameraTrack} cameraOn={cameraOn} micOn={micOn} playAudio={false} style={{ width: '100%', height: '100%' }} />
            </Box>
          )}
        </Box>

        {/* Controls Bar */}
        <Box className="no-drag" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#3c3c3c', py: 1, height: '75px', flexShrink: 0 }}>
          <Stack direction="row" spacing={3}>
            <IconButton onClick={() => setMic((prev) => !prev)} sx={{ bgcolor: micOn ? '#4caf50' : '#f44336', color: 'white', '&:hover': { bgcolor: micOn ? '#388e3c' : '#d32f2f' }, p: 1.5 }}>
              {micOn ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
            <IconButton onClick={handleEndCall} sx={{ bgcolor: '#f44336', color: '#fff', '&:hover': { bgcolor: '#d32f2f' }, transform: 'scale(1.2)', mx: 2 }}>
              <CallEndIcon />
            </IconButton>
            <IconButton onClick={() => setCamera((prev) => !prev)} sx={{ bgcolor: cameraOn ? '#4caf50' : '#f44336', color: 'white', '&:hover': { bgcolor: cameraOn ? '#388e3c' : '#d32f2f' }, p: 1.5 }}>
              {cameraOn ? <VideocamIcon /> : <VideocamOffIcon />}
            </IconButton>
          </Stack>
        </Box>

        {/* --- Resizable Handle --- */}
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '20px',
            height: '20px',
            cursor: 'nwse-resize',
            '&:hover': {
                borderRight: '3px solid #4caf50',
                borderBottom: '3px solid #4caf50',
            }
          }}
        />
      </Box>
    </Modal>
  );
};
